import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport
} from '@mui/x-data-grid';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ApiIcon from '@mui/icons-material/Api';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAdminStats, getAllFeedbacks, getApiUsageSummary, getPopularFeatures, getAllAnalysisSessions, getErrorLogs, getRaceTypes, getRaces, uploadRaceGpx, deleteRaceGpx } from '../api/admin';
import AdminGeoHeatMap from '../components/AdminGeoHeatMap';

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [analysisSessions, setAnalysisSessions] = useState([]);
  const [totalAnalysisSessions, setTotalAnalysisSessions] = useState(0);
  const [errorLogs, setErrorLogs] = useState([]);
  const [totalErrorLogs, setTotalErrorLogs] = useState(0);
  const [apiUsage, setApiUsage] = useState(null);
  const [popularFeatures, setPopularFeatures] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [analysisSessionsPaginationModel, setAnalysisSessionsPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [errorLogsPaginationModel, setErrorLogsPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // GPX Upload state
  const [races, setRaces] = useState([]);
  const [raceTypes, setRaceTypes] = useState([]);
  const [selectedRaceType, setSelectedRaceType] = useState('');
  const [selectedRace, setSelectedRace] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
    loadRaces();
  }, [paginationModel, analysisSessionsPaginationModel, errorLogsPaginationModel]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load stats
      const statsData = await getAdminStats();
      setStats(statsData);

      // Load feedbacks
      const feedbackData = await getAllFeedbacks(
        paginationModel.page * paginationModel.pageSize,
        paginationModel.pageSize
      );
      setFeedbacks(feedbackData.feedbacks);
      setTotalFeedbacks(feedbackData.total);

      // Load analysis sessions
      const analysisSessionsData = await getAllAnalysisSessions(
        analysisSessionsPaginationModel.page * analysisSessionsPaginationModel.pageSize,
        analysisSessionsPaginationModel.pageSize
      );
      setAnalysisSessions(analysisSessionsData.analysis_sessions);
      setTotalAnalysisSessions(analysisSessionsData.total);

      // Load error logs
      const errorLogsData = await getErrorLogs(
        errorLogsPaginationModel.page * errorLogsPaginationModel.pageSize,
        errorLogsPaginationModel.pageSize
      );
      setErrorLogs(errorLogsData.error_logs);
      setTotalErrorLogs(errorLogsData.total);

      // Load API usage data (last 30 days)
      const apiUsageData = await getApiUsageSummary(30);
      setApiUsage(apiUsageData);

      // Load popular features (last 30 days)
      const popularFeaturesData = await getPopularFeatures(30);
      setPopularFeatures(popularFeaturesData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Admin privileges required.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRaces = async () => {
    try {
      const raceTypesData = await getRaceTypes();
      setRaceTypes(raceTypesData || []);

      // Load all races by fetching multiple pages if needed
      let allRaces = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 100; // Maximum allowed by the backend

      while (hasMore) {
        const racesData = await getRaces({ page, page_size: pageSize });
        allRaces = [...allRaces, ...(racesData.races || [])];

        // Check if there are more pages
        hasMore = allRaces.length < racesData.total;
        page++;
      }

      setRaces(allRaces);
    } catch (error) {
      console.error('Failed to load races:', error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.gpx')) {
      setUploadFile(file);
    } else {
      setSnackbar({ open: true, message: 'Please select a valid GPX file', severity: 'error' });
      event.target.value = '';
    }
  };

  const handleUploadGpx = async () => {
    if (!selectedRace || !uploadFile) {
      setSnackbar({ open: true, message: 'Please select a race and a GPX file', severity: 'warning' });
      return;
    }

    try {
      setUploadLoading(true);
      await uploadRaceGpx(selectedRace, uploadFile);
      setSnackbar({ open: true, message: 'GPX file uploaded successfully!', severity: 'success' });
      setUploadFile(null);
      setSelectedRace('');
      // Reset file input
      const fileInput = document.getElementById('gpx-file-input');
      if (fileInput) fileInput.value = '';
      // Reload races to update has_gpx status
      await loadRaces();
    } catch (error) {
      console.error('Failed to upload GPX:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to upload GPX file',
        severity: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteGpx = async () => {
    if (!selectedRace) {
      setSnackbar({ open: true, message: 'Please select a race', severity: 'warning' });
      return;
    }

    const selectedRaceObj = races.find(r => r.id === selectedRace);
    if (!selectedRaceObj?.has_gpx) {
      setSnackbar({ open: true, message: 'This race has no GPX file to delete', severity: 'warning' });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the GPX file for ${selectedRaceObj.name}?`)) {
      return;
    }

    try {
      setUploadLoading(true);
      await deleteRaceGpx(selectedRace);
      setSnackbar({ open: true, message: 'GPX file deleted successfully!', severity: 'success' });
      // Reload races to update has_gpx status
      await loadRaces();
    } catch (error) {
      console.error('Failed to delete GPX:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete GPX file',
        severity: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFeedbackClick = (params) => {
    navigate(`/admin/feedback/${params.row.id}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'error',
      reviewing: 'warning',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'user_email',
      headerName: 'User',
      width: 200,
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'fit_filename',
      headerName: 'FIT File',
      width: 150,
    },
    {
      field: 'gpx_filename',
      headerName: 'GPX File',
      width: 150,
    },
    {
      field: 'total_distance_km',
      headerName: 'Distance (km)',
      width: 120,
      valueFormatter: (value) => value != null ? Number(value).toFixed(2) : '-',
    },
    {
      field: 'avg_power',
      headerName: 'Avg Power',
      width: 100,
      valueFormatter: (value) => value != null ? `${Number(value).toFixed(0)}W` : '-',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
  ];

  const analysisSessionsColumns = [
    {
      field: 'id',
      headerName: 'Session ID',
      width: 120,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      type: "dateTime",
      valueGetter: ( value ) => value && new Date(value),
    },
    {
      field: 'user_email',
      headerName: 'User',
      width: 200,
    },
    {
      field: 'fit_filename',
      headerName: 'FIT File',
      width: 150,
    },
    {
      field: 'gpx_filename',
      headerName: 'GPX File',
      width: 150,
    },
    {
      field: 'total_distance_km',
      headerName: 'Distance (km)',
      width: 120,
      type: 'number',
    },
    {
      field: 'total_duration_sec',
      headerName: 'Duration',
      width: 100,
    },
    {
      field: 'avg_power',
      headerName: 'Avg Power',
      width: 100,
      type: 'number',
    },
    {
      field: 'curvature_score',
      headerName: 'Curvature',
      width: 100,
      type: 'number',
    },
    {
      field: 'stamina_score',
      headerName: 'Stamina',
      width: 100,
      type: 'number',
    },
    {
      field: 'num_fit_chunks',
      headerName: 'FIT Chunks',
      width: 100,
    },
    {
      field: 'num_gpx_segments',
      headerName: 'GPX Segments',
      width: 120,
    },
    {
      field: 'feedback_count',
      headerName: 'Feedbacks',
      width: 100,
    },

  ];

  const errorLogsColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'timestamp',
      headerName: 'Time',
      width: 180,
      // valueFormatter: (params) => params?.value ? new Date(params.value).toLocaleString() : '-',
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 80,
    },
    {
      field: 'path',
      headerName: 'Path',
      width: 200,
    },
    {
      field: 'status_code',
      headerName: 'Status',
      width: 80,
    },
    {
      field: 'response_time_ms',
      headerName: 'Response Time',
      width: 120,
      // valueFormatter: (params) => params?.value ? `${params.value.toFixed(0)}ms` : '-',
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      width: 100,
      // valueFormatter: (params) => params?.value || '-',
    },
    {
      field: 'ip_address',
      headerName: 'IP',
      width: 120,
    },
    {
      field: 'error_message',
      headerName: 'Error',
      width: 300,
    },
  ];

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      {/* GPX Upload Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Upload Official Race GPX Files
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Upload or update official GPX course files for races. This will overwrite any existing file.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Race Type</InputLabel>
            <Select
              value={selectedRaceType}
              onChange={(e) => {
                setSelectedRaceType(e.target.value);
                setSelectedRace(''); // Reset race selection when type changes
                setUploadFile(null); // Reset file selection
              }}
              label="Race Type"
            >
              <MenuItem value="">
                <em>Select race type...</em>
              </MenuItem>
              {raceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedRaceType && (
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Race</InputLabel>
              <Select
                value={selectedRace}
                onChange={(e) => setSelectedRace(e.target.value)}
                label="Select Race"
              >
                <MenuItem value="">
                  <em>Select a race...</em>
                </MenuItem>
                {races
                  .filter((race) => race.race_type_id === selectedRaceType)
                  .map((race) => (
                    <MenuItem key={race.id} value={race.id}>
                      {race.name} ({race.country})
                      {race.has_gpx && <CheckCircleIcon sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {selectedRace && (
            <>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={uploadLoading}
              >
                Choose GPX File
                <input
                  id="gpx-file-input"
                  type="file"
                  accept=".gpx"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>

              {uploadFile && (
                <Chip
                  label={uploadFile.name}
                  onDelete={() => setUploadFile(null)}
                  color="primary"
                  variant="outlined"
                />
              )}

              <Button
                variant="contained"
                onClick={handleUploadGpx}
                disabled={!uploadFile || uploadLoading}
                startIcon={uploadLoading ? <CircularProgress size={20} /> : <UploadFileIcon />}
              >
                Upload
              </Button>

              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteGpx}
                disabled={uploadLoading}
                startIcon={<DeleteIcon />}
              >
                Delete GPX
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {(stats?.total_users || 0) + (stats?.total_anonymous_sessions || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stats?.total_users || 0} auth + {stats?.total_anonymous_sessions || 0} anon
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Feedbacks
                  </Typography>
                  <Typography variant="h4">
                    {stats?.total_feedbacks || 0}
                  </Typography>
                </Box>
                <FeedbackIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    API Requests (30d)
                  </Typography>
                  <Typography variant="h4">
                    {apiUsage?.total_requests?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <ApiIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Response Time
                  </Typography>
                  <Typography variant="h4">
                    {apiUsage?.avg_response_time_ms ? `${Math.round(apiUsage.avg_response_time_ms)}ms` : '-'}
                  </Typography>
                </Box>
                <SpeedIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Error Rate (30d)
                  </Typography>
                  <Typography variant="h4">
                    {apiUsage?.error_rate ? `${apiUsage.error_rate}%` : '0%'}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Users (30d)
                  </Typography>
                  <Typography variant="h4">
                    {apiUsage?.total_unique_users || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {apiUsage?.unique_authenticated_users || 0} auth + {apiUsage?.unique_anonymous_users || 0} anon
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              New Users per Week
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats?.new_users_per_week || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#8884d8" name="New Users" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Login Sessions per Week
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats?.sessions_per_week || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#82ca9d" name="Sessions" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              API Requests per Day (Last 30 Days)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={apiUsage?.requests_by_day || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#ff7300" name="Requests" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Most Popular Features (Last 30 Days)
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {popularFeatures?.features?.map((feature, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2">{feature.feature}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {feature.total_requests.toLocaleString()} requests
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="textSecondary">
                      {feature.unique_users} unique users
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(feature.avg_response_time_ms)}ms avg
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 4,
                      bgcolor: 'grey.300',
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min((feature.total_requests / (popularFeatures?.features?.[0]?.total_requests || 1)) * 100, 100)}%`,
                        height: '100%',
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Top API Endpoints */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Top API Endpoints (Last 30 Days)
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <Box display="grid" gridTemplateColumns="auto 1fr 100px 120px" gap={2} sx={{ mb: 1, fontWeight: 'bold', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
            <Typography variant="body2">Method</Typography>
            <Typography variant="body2">Endpoint</Typography>
            <Typography variant="body2" textAlign="right">Requests</Typography>
            <Typography variant="body2" textAlign="right">Avg Time</Typography>
          </Box>
          {apiUsage?.requests_by_endpoint?.slice(0, 15).map((endpoint, index) => (
            <Box
              key={index}
              display="grid"
              gridTemplateColumns="auto 1fr 100px 120px"
              gap={2}
              sx={{
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Chip label={endpoint.method} size="small" color="primary" sx={{ width: 70 }} />
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {endpoint.path}
              </Typography>
              <Typography variant="body2" textAlign="right" fontWeight="bold">
                {endpoint.count.toLocaleString()}
              </Typography>
              <Typography variant="body2" textAlign="right" color="textSecondary">
                {Math.round(endpoint.avg_response_time_ms)}ms
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Feedbacks DataGrid */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          All Feedbacks
        </Typography>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={feedbacks}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalFeedbacks}
            paginationMode="server"
            loading={loading}
            onRowClick={handleFeedbackClick}
            slots={{
              toolbar: CustomToolbar,
            }}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Analysis Sessions DataGrid */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            All Analysis Sessions
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={analysisSessions}
            columns={analysisSessionsColumns}
            paginationModel={analysisSessionsPaginationModel}
            onPaginationModelChange={setAnalysisSessionsPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalAnalysisSessions}
            paginationMode="server"
            loading={loading}
            slots={{
              toolbar: CustomToolbar,
            }}
            getRowId={(row) => row.id}
            initialState={{
              sorting: {
                sortModel: [{ field: 'created_at', sort: 'desc' }],
              },
            }}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Error Logs DataGrid */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Error Logs
        </Typography>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={errorLogs}
            columns={errorLogsColumns}
            paginationModel={errorLogsPaginationModel}
            onPaginationModelChange={setErrorLogsPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalErrorLogs}
            paginationMode="server"
            loading={loading}
            slots={{
              toolbar: CustomToolbar,
            }}
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      <AdminGeoHeatMap />
    </Container>
  );
};

export default AdminDashboard;
